/**
 * Hints in vs-computer games (docs/HINTS-LAYOUT-TUTORIAL.md A1): the engine's
 * hard bot picks the move (suggestAction), this file says it in Tibbs's words
 * and works out which thing on the table to mark.
 */
import type { GameState, HintReason, Suggestion } from '../../engine';
import { fmt } from '../brand';

/** Tibbs's line per reason. {v} = the card the advice is about, {o} = the card it beats or pairs. */
export const HINT_COPY: Record<HintReason, string> = {
  deckOnly: 'Draw from the deck.',
  deckBetter: 'Draw from the deck. The {v} won’t help.',
  takePair: 'Take the {v}. It matches your column.',
  takeBonus: 'Take the {v} for a bonus column.',
  takeLow: 'Take the {v}. Low cards win holes.',
  takeBetter: 'Take the {v}. It beats your {o}.',
  takeGood: 'Take the {v}. Better than a gamble.',
  swapOut: 'Swap it in and finish the hole.',
  swapPair: 'Swap it here to match this column.',
  swapBonus: 'Swap it here for a bonus column.',
  swapHigh: 'Swap it for your {o}.',
  swapHidden: 'Swap it for a hidden card.',
  discard: 'Not worth keeping. Discard it.',
  flipOut: 'Flip it and finish the hole.',
  flipPartner: 'Flip here. It could pair your {o}.',
  flipAny: 'Any hidden card will do.',
  keepHidden: 'Keep it hidden. You’d finish behind.',
};

export function hintText(s: Suggestion): string {
  return HINT_COPY[s.reason].replace('{v}', s.value !== undefined ? fmt(s.value) : '').replace('{o}', s.other !== undefined ? fmt(s.other) : '');
}

/** What the hint marks on the table. */
export type HintTarget = { kind: 'deck' } | { kind: 'discard' } | { kind: 'keep' } | { kind: 'slot'; slot: number };

export function hintTarget(s: Suggestion): HintTarget | null {
  const a = s.action;
  switch (a.type) {
    case 'drawDeck':
      return { kind: 'deck' };
    case 'drawDiscard':
    case 'discardDrawn':
      return { kind: 'discard' };
    case 'skip':
      return { kind: 'keep' };
    case 'swap':
    case 'flip':
      return { kind: 'slot', slot: a.slot };
    default:
      return null;
  }
}

/** One hint per decision: a new event (any move) makes the old one stale. */
export const hintKey = (s: GameState): string => `${s.hole}:${s.eventSeq}`;

/** The Hint pill shows only on my own turn in a vs-computer game, with nothing else in the way (A1.2). */
export function hintAvailable(o: {
  mode: 'solo' | 'online';
  guided: boolean;
  setting: boolean;
  state: GameState;
  me: number | null;
  /** A coach step, the travel scene, the hole end, or a sheet is up. */
  busy: boolean;
}): boolean {
  const { state, me } = o;
  if (o.mode !== 'solo' || o.guided || !o.setting || o.busy || me === null) return false;
  if (state.phase !== 'playing' && state.phase !== 'finalTurns') return false;
  return state.current === me && (state.stage === 'draw' || state.stage === 'holding' || state.stage === 'flip');
}
